import React from 'react';
import { ChartType } from '../../types/store/normalized/insights/chartTypes';
import { ChartProps } from './types';
import GenericDonutChart from './Charts/GenericDonutChart';
import GenericHorizontalBarChart from './Charts/GenericHorizontalBarChart';
import GenericVerticalBarChart from './Charts/GenericVerticalBarChart';
import GenericHorizontalLineChart from './Charts/GenericHorizontalLineChart';
import GenericKPIChart from './Charts/GenericKPIChart';
import { InsightDrilldownSelectedData } from '../../types/store/normalized/insights/insightFilterMetadata';

export type DonutChartData = Array<{
  [k: string]: number | string;
}>;

export type BarChartData = Array<{
  [k: string]: number | string;
}>;

export type LineChartData = Array<{
  [k: string]: number | string;
}>;

export type KPIChartData = {
  value: number;
  trend: number;
  trendIsPercent: boolean;
  description: string;
};

export type OnDrillDown = (
  drilldownKey?: string,
  data?: InsightDrilldownSelectedData
) => void;

export type ChartData =
  | DonutChartData
  | BarChartData
  | LineChartData
  | KPIChartData;

export type DonutChartProps = ChartProps<DonutChartData> & {
  categoryFieldName: string;
  valueFieldName: string;
  onDrillDown?: OnDrillDown;
};

export type xAxisOptions = {
  label: {
    align?: 'left' | 'right' | 'center';
    formatter?: (value: unknown) => string;
  };
  min?: number;
  max?: number;
};

export type HorizontalBarChartProps = ChartProps<BarChartData> & {
  xAxisOptions?: xAxisOptions;
  xAxisFieldNames: string[];
  yAxisFieldName: string;
  legendNames: string[];
  stacks?: string[];
  stackTotalFieldName?: string;
  lineFieldNames?: string[];
  lineLegendNames?: string[];
  lineAxisName?: string;
  barAxisName?: string;
  yAxisName?: string;
  onDrillDown?: OnDrillDown;
  markLines?: number[];
  gridPadding?: [
    number | undefined,
    number | undefined,
    number | undefined,
    number | undefined
  ];
};

export type VerticalBarChartProps = ChartProps<BarChartData> & {
  xAxisFieldName: string;
  yAxisFieldNames: string[];
  legendNames: string[];
  stacks?: string[];
  lineFieldNames?: string[];
  lineLegendNames?: string[];
  lineAxisName?: string;
  barAxisName?: string;
  onDrillDown?: OnDrillDown;
};

export type LineChartProps = ChartProps<LineChartData> & {
  xAxisFieldName: string;
  yAxisFieldNames: string[];
  legendNames: string[];
  lineFieldNames?: string[];
  lineLegendNames?: string[];
  lineAxisName?: string;
  onDrillDown?: OnDrillDown;
};

export type KPIChartProps = ChartProps<KPIChartData>;

export type ChartPropsUnion =
  | DonutChartProps
  | HorizontalBarChartProps
  | VerticalBarChartProps
  | LineChartProps
  | KPIChartProps;

const CHART_COMPONENTS: Record<ChartType, React.ComponentType<any>> = {
  [ChartType.DONUT]: GenericDonutChart,
  [ChartType.H_BAR]: GenericHorizontalBarChart,
  [ChartType.H_BAR_MULTI]: GenericHorizontalBarChart,
  [ChartType.H_BAR_STACK]: GenericHorizontalBarChart,
  [ChartType.V_BAR]: GenericVerticalBarChart,
  [ChartType.V_BAR_MULTI]: GenericVerticalBarChart,
  [ChartType.V_BAR_STACK]: GenericVerticalBarChart,
  [ChartType.V_BAR_STACK_LINE]: GenericVerticalBarChart,
  [ChartType.LINE]: GenericHorizontalLineChart,
  [ChartType.LINE_MULTI]: GenericHorizontalLineChart,
  [ChartType.KPI]: GenericKPIChart,
};

type ChartTypeToSpecificProps<T extends ChartType> = T extends ChartType.DONUT
  ? DonutChartProps
  : T extends ChartType.H_BAR | ChartType.H_BAR_MULTI | ChartType.H_BAR_STACK
  ? HorizontalBarChartProps
  : T extends
      | ChartType.V_BAR
      | ChartType.V_BAR_MULTI
      | ChartType.V_BAR_STACK
      | ChartType.V_BAR_STACK_LINE
  ? VerticalBarChartProps
  : T extends ChartType.LINE | ChartType.LINE_MULTI
  ? LineChartProps
  : T extends ChartType.KPI
  ? KPIChartProps
  : never;

/* This is part of the Typesafe Builder Pattern
 *
 * This is the starting point for building charts. It doesn't know the chart type yet.
 */
export class ChartBuilder {
  /**
   * Sets the chart type and returns a typed builder for that specific chart type
   */
  setChartType<T extends ChartType>(chartType: T): TypedChartBuilder<T> {
    return new TypedChartBuilder<T>(chartType);
  }
}

/**
 * Typed Chart Builder
 *
 * This builder knows the specific chart type and can only accept props for that chart type.
 */
export class TypedChartBuilder<T extends ChartType> {
  private readonly chartType: T;
  private readonly Component: React.ComponentType<ChartTypeToSpecificProps<T>>;

  constructor(chartType: T) {
    this.chartType = chartType;

    // Get the component for this chart type - TypeScript ensures this exists
    this.Component = CHART_COMPONENTS[chartType] as React.ComponentType<
      ChartTypeToSpecificProps<T>
    >;
  }

  /**
   * Sets the props for the chart. The props type is automatically inferred from the chart type.
   */
  setProps(props: ChartTypeToSpecificProps<T>): FinalChartBuilder<T> {
    return new FinalChartBuilder<T>(this.chartType, this.Component, props);
  }

  /**
   * Gets the chart type this builder is configured for
   */
  getChartType(): T {
    return this.chartType;
  }
}

/**
 * Final Chart Builder
 *
 * This builder has both the chart type and props, and can build the final chart.
 */
export class FinalChartBuilder<T extends ChartType> {
  private readonly chartType: T;
  private readonly Component: React.ComponentType<ChartTypeToSpecificProps<T>>;
  private readonly props: ChartTypeToSpecificProps<T>;

  constructor(
    chartType: T,
    Component: React.ComponentType<ChartTypeToSpecificProps<T>>,
    props: ChartTypeToSpecificProps<T>
  ) {
    this.chartType = chartType;
    this.Component = Component;
    this.props = props;
  }

  /**
   * Builds and returns the chart component
   *
   * @returns A React element for the specified chart type
   */
  build(): React.ReactElement {
    const Component = this.Component as React.ComponentType<any>;
    return React.createElement(Component, this.props);
  }

  getChartType(): T {
    return this.chartType;
  }

  getProps(): ChartTypeToSpecificProps<T> {
    return this.props;
  }
}

/**
 * Factory function to create a new TypeSafeChartBuilder
 *
 * @returns A new TypeSafeChartBuilder instance
 */
export function createTypeSafeChartBuilder(): ChartBuilder {
  return new ChartBuilder();
}

/**
 * Hook for using a TypeSafeChartBuilder
 *
 * @returns A TypeSafeChartBuilder instance
 */
export function useTypeSafeChartBuilder(): ChartBuilder {
  return React.useMemo(() => createTypeSafeChartBuilder(), []);
}

export function buildChart<T extends ChartType>(
  chartType: T,
  props: ChartTypeToSpecificProps<T>
): React.ReactElement {
  return new ChartBuilder().setChartType(chartType).setProps(props).build();
}

export default ChartBuilder;
